import React, {
  useState,
  useRef,
  useEffect,
} from "react";

import styles from "./PartnerLoginInfo.module.css";
import SplashScreen from "../../SplashScreen";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

export default function PartnerLoginInfo({
  onBack,
  onSave,
}) {
  const navigate = useNavigate();

  const {
    userId,
    deviceUuid,
  } = useParams();

  /* =========================================================
     API
  ========================================================= */

  const API_URL =
    "https://coinyoco.world/brobizee-api/partnerdata.php";

  const API_URLA =
    "https://coinyoco.world/brobizee-api/get_user.php";

  /* =========================================================
     URL PATH
  ========================================================= */

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  const goTo = (path) => {
    navigate(
      basePath
        ? `${basePath}${path}`
        : path
    );
  };

  /* =========================================================
     STORAGE KEY
  ========================================================= */

  const STORAGE_KEY =
    userId && deviceUuid
      ? `partnerProfile_${userId}_${deviceUuid}`
      : "partnerProfile";

  /* =========================================================
     FILE INPUT
  ========================================================= */

  const fileInputRef = useRef(null);

  /* =========================================================
     PROFILE IMAGE
  ========================================================= */

  const [profileImage, setProfileImage] =
    useState(null);

  const [profileFile, setProfileFile] =
    useState(null);

  /* =========================================================
     USER DATA
  ========================================================= */

  const [user, setUser] = useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  /* =========================================================
     FORM DATA
  ========================================================= */

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    address: "",
    age: "",
    gender: "Male",
    bio: "",
  });

  /* =========================================================
     PASSWORD
  ========================================================= */

  const [showPassword, setShowPassword] =
    useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  /* =========================================================
     ERRORS
  ========================================================= */

  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    address: "",
    age: "",
    gender: "",
    bio: "",
    profileImage: "",
  });

  /* =========================================================
     SUBMIT
  ========================================================= */

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  /* =========================================================
     GET USER ID
  ========================================================= */

  const storedUserId =
    localStorage.getItem("user_id");

  const currentUserId =
    userId || storedUserId;

  /* =========================================================
     RESTORE PARTNER DATA
  ========================================================= */

  useEffect(() => {
    try {
      const saved =
        localStorage.getItem(STORAGE_KEY);

      if (!saved) {
        return;
      }

      const parsed =
        JSON.parse(saved);

      if (!parsed) {
        return;
      }

      setFormData((previous) => ({
        ...previous,

        name:
          parsed.full_name ??
          parsed.name ??
          previous.name,

        phone:
          parsed.phone_number ??
          parsed.phone ??
          previous.phone,

        email:
          parsed.email_id ??
          parsed.email ??
          previous.email,

        password:
          parsed.password ??
          previous.password,

        confirmPassword:
          parsed.confirm_password ??
          parsed.confirmPassword ??
          previous.confirmPassword,

        address:
          parsed.address ??
          previous.address,

        age:
          parsed.age ??
          previous.age,

        gender:
          parsed.gender ??
          previous.gender,

        bio:
          parsed.bio ??
          previous.bio,
      }));
    } catch (restoreError) {
      console.error(
        "Unable to restore partner data:",
        restoreError
      );
    }
  }, [STORAGE_KEY]);

  /* =========================================================
     FETCH USER DATA
  ========================================================= */

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError("");

        if (!currentUserId) {
          throw new Error(
            "User ID not found in localStorage or URL."
          );
        }

        console.log(
          "Fetching user:",
          currentUserId
        );

        const response = await fetch(
          `${API_URLA}?user_id=${encodeURIComponent(
            currentUserId
          )}`
        );

        const responseText =
          await response.text();

        console.log(
          "RAW GET USER RESPONSE:",
          responseText
        );

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${responseText}`
          );
        }

        let data;

        try {
          data =
            JSON.parse(responseText);
        } catch (jsonError) {
          console.error(
            "Invalid JSON:",
            responseText
          );

          throw new Error(
            "PHP API returned invalid JSON."
          );
        }

        console.log(
          "GET USER RESULT:",
          data
        );

        if (!data.success) {
          throw new Error(
            data.message ||
              "Unable to fetch user data."
          );
        }

        if (!isMounted) {
          return;
        }

        const fetchedUser =
          data.user || {};

        setUser(fetchedUser);

        /* =====================================================
           POPULATE FORM FROM USER TABLE
        ===================================================== */

        setFormData((previous) => ({
          ...previous,

          name:
            fetchedUser.name ||
            previous.name ||
            "",

          phone:
            fetchedUser.mobile_number ||
            previous.phone ||
            "",

          email:
            fetchedUser.email ||
            fetchedUser.email_id ||
            previous.email ||
            "",
        }));
      } catch (fetchError) {
        console.error(
          "Fetch user error:",
          fetchError
        );

        if (isMounted) {
          setError(
            fetchError.message ||
              "Something went wrong while loading user data."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [currentUserId]);

  /* =========================================================
     INPUT CHANGE
  ========================================================= */

  const handleChange = (e) => {
  const { name, value } = e.target;

  let newValue = value;

  /* =====================================================
     PHONE: allow only digits and optional leading "+"
     max 13 characters
  ===================================================== */
  if (name === "phone") {
    // keep only digits and "+"
    newValue = value.replace(/[^\d+]/g, "");

    // "+" only allowed at start
    if (newValue.indexOf("+") > 0) {
      newValue = newValue.replace(/\+/g, "");
    }

    // only one "+"
    if (newValue.startsWith("+")) {
      newValue =
        "+" +
        newValue.slice(1).replace(/\+/g, "");
    }

    // max 13 chars total
    newValue = newValue.slice(0, 13);
  }

  setFormData((previous) => ({
    ...previous,
    [name]: newValue,
  }));

  setErrors((previous) => ({
    ...previous,
    [name]: "",
  }));
};

  /* =========================================================
     IMAGE CHANGE
  ========================================================= */

  const handleImageChange = (e) => {
    const file =
      e.target.files?.[0];

    if (!file) {
      return;
    }

    /* =====================================================
       FILE TYPE
    ===================================================== */

    if (!file.type.startsWith("image/")) {
      setErrors((previous) => ({
        ...previous,
        profileImage:
          "Please select a valid image.",
      }));

      return;
    }

    /* =====================================================
       FILE SIZE
    ===================================================== */

    if (file.size > 5 * 1024 * 1024) {
      setErrors((previous) => ({
        ...previous,
        profileImage:
          "Image size must be less than 5 MB.",
      }));

      return;
    }

    /* =====================================================
       REMOVE OLD OBJECT URL
    ===================================================== */

    if (profileImage) {
      URL.revokeObjectURL(profileImage);
    }

    /* =====================================================
       CREATE PREVIEW
    ===================================================== */

    const imageUrl =
      URL.createObjectURL(file);

    setProfileImage(imageUrl);
    setProfileFile(file);

    setErrors((previous) => ({
      ...previous,
      profileImage: "",
    }));
  };

  /* =========================================================
     CLEAN IMAGE URL
  ========================================================= */

  useEffect(() => {
    return () => {
      if (profileImage) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  /* =========================================================
     PASSWORD VALIDATION
  ========================================================= */

  const validatePassword = (password) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/
      .test(password);
  };

  /* =========================================================
     FORM VALIDATION
  ========================================================= */

  const validateForm = () => {
    const newErrors = {
      name: "",
      phone: "",
      email: "",
      password: "",
      confirmPassword: "",
      address: "",
      age: "",
      gender: "",
      bio: "",
      profileImage: "",
    };

    /* =====================================================
       IDENTIFIERS
    ===================================================== */

    if (!userId) {
      console.warn(
        "userId missing from URL."
      );
    }

    if (!deviceUuid) {
      console.warn(
        "deviceUuid missing from URL."
      );
    }

    /* =====================================================
       NAME
    ===================================================== */

    if (!formData.name.trim()) {
      newErrors.name =
        "Please enter your full name.";
    } else if (
      formData.name.trim().length < 3
    ) {
      newErrors.name =
        "Name must be at least 3 characters.";
    }

    /* =====================================================
       PHONE
    ===================================================== */

    const phone =
      formData.phone.replace(/\D/g, "");

    if (!phone) {
      newErrors.phone =
        "Please enter your phone number.";
    } else if (
       !/^\+?\d+$/.test(phone)
    ) {
      newErrors.phone =
        "Please enter a valid 10-digit phone number.";
    }

    /* =====================================================
       EMAIL
    ===================================================== */

    const email =
      formData.email.trim();

    if (!email) {
      newErrors.email =
        "Please enter your email address.";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        email
      )
    ) {
      newErrors.email =
        "Please enter a valid email address.";
    }

    /* =====================================================
       PASSWORD
    ===================================================== */

    if (!formData.password) {
      newErrors.password =
        "Please create a password.";
    } else if (
      !validatePassword(
        formData.password
      )
    ) {
      newErrors.password =
        "Password must contain 8+ characters, uppercase, lowercase, number and special character.";
    }

    /* =====================================================
       CONFIRM PASSWORD
    ===================================================== */

    if (!formData.confirmPassword) {
      newErrors.confirmPassword =
        "Please confirm your password.";
    } else if (
      formData.password !==
      formData.confirmPassword
    ) {
      newErrors.confirmPassword =
        "Passwords do not match.";
    }

    /* =====================================================
       ADDRESS
    ===================================================== */

    if (!formData.address.trim()) {
      newErrors.address =
        "Please enter your address.";
    } else if (
      formData.address.trim().length < 10
    ) {
      newErrors.address =
        "Please enter a complete address.";
    }

    /* =====================================================
       AGE
    ===================================================== */

    const age =
      Number(formData.age);

    if (!formData.age) {
      newErrors.age =
        "Please enter your age.";
    } else if (
      Number.isNaN(age) ||
      age < 18 ||
      age > 100
    ) {
      newErrors.age =
        "Age must be between 18 and 100.";
    }

    /* =====================================================
       GENDER
    ===================================================== */

    if (!formData.gender) {
      newErrors.gender =
        "Please select your gender.";
    }

    /* =====================================================
       BIO
    ===================================================== */

    if (!formData.bio.trim()) {
      newErrors.bio =
        "Please tell us about your work.";
    } else if (
      formData.bio.trim().length < 20
    ) {
      newErrors.bio =
        "Bio must be at least 20 characters.";
    }

    setErrors(newErrors);

    return !Object.values(
      newErrors
    ).some(
      (value) => value !== ""
    );
  };

  /* =========================================================
     GET CURRENT GPS LOCATION
  ========================================================= */

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.warn(
          "Browser does not support GPS location."
        );

        resolve("");

        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latitude =
            position.coords.latitude;

          const longitude =
            position.coords.longitude;

          const latitudeLongitude =
            `${latitude},${longitude}`;

          console.log(
            "PARTNER GPS LOCATION:",
            latitudeLongitude
          );

          resolve(
            latitudeLongitude
          );
        },

        (locationError) => {
          console.warn(
            "Unable to get GPS location:",
            locationError.message
          );

          /*
            GPS permission deny/error hone par
            registration stop nahi hoga.
          */

          resolve("");
        },

        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    });
  };

  /* =========================================================
     SAVE PARTNER DATA
  ========================================================= */

  const savePartnerData = async () => {
    /*
      =====================================================
      GET CURRENT GPS LOCATION
      =====================================================
    */

    const currentLatitudeLongitude =
      await getCurrentLocation();

    console.log(
      "Location before partner save:",
      currentLatitudeLongitude
    );

    /*
      =====================================================
      RESTORE PREVIOUS DATA
      =====================================================
    */

    let previousData = {};

    try {
      const saved =
        localStorage.getItem(
          STORAGE_KEY
        );

      if (saved) {
        previousData =
          JSON.parse(saved) || {};
      }
    } catch (restoreError) {
      console.error(
        "Unable to read previous partner data:",
        restoreError
      );
    }

    /*
      =====================================================
      MERGE DATA
      =====================================================
    */

    const dataToSave = {
      ...previousData,

      /* ===================================================
         IDENTIFIERS
      =================================================== */

      secondary_id:
        userId ||
        previousData.secondary_id ||
        "",

      secondary_uuid:
        deviceUuid ||
        previousData.secondary_uuid ||
        "",

      /* ===================================================
         STATUS
      =================================================== */

      stutes:
        previousData.stutes ||
        "",

      online_stutes:
        previousData.online_stutes ||
        "",

      /* ===================================================
         PROFILE
      =================================================== */

      profile:
        previousData.profile ||
        "",

      /* ===================================================
         PERSONAL INFORMATION
      =================================================== */

      full_name:
        formData.name.trim(),

      phone_number:
        formData.phone.replace(
          /\D/g,
          ""
        ),

      email_id:
        formData.email.trim(),

      password:
        formData.password,

      confirm_password:
        formData.confirmPassword,

      address:
        formData.address.trim(),

      /* ===================================================
         GPS LOCATION
      =================================================== */

      latitude_longitude:
        currentLatitudeLongitude ||
        previousData.latitude_longitude ||
        "",

      /* ===================================================
         AGE / GENDER / BIO
      =================================================== */

      age:
        formData.age,

      gender:
        formData.gender,

      bio:
        formData.bio.trim(),

      /* ===================================================
         WORK
      =================================================== */

      work:
        previousData.work ||
        "",

      experiences:
        previousData.experiences ||
        "",

      /* ===================================================
         DOCUMENT
      =================================================== */

      document_type:
        previousData.document_type ||
        "",

      upload_documents_images1:
        previousData.upload_documents_images1 ||
        "",

      upload_documents_images2:
        previousData.upload_documents_images2 ||
        "",

      /* ===================================================
         BANK
      =================================================== */

      account_type:
        previousData.account_type ||
        "",

      account_holder_name:
        previousData.account_holder_name ||
        "",

      account_number:
        previousData.account_number ||
        "",

      ifsc_code:
        previousData.ifsc_code ||
        "",

      /* ===================================================
         MONEY
      =================================================== */

      profit:
        previousData.profit ??
        0,

      wallet:
        previousData.wallet ??
        0,

      money:
        previousData.money ??
        0,

      invest:
        previousData.invest ??
        0,

      upi_id:
        previousData.upi_id ||
        "",

      /* ===================================================
         OTHER
      =================================================== */

      refrences:
        previousData.refrences ||
        "",

      recently_work:
        previousData.recently_work ||
        "",

      secondary_work:
        previousData.secondary_work ||
        "",

      helper_list:
        previousData.helper_list ||
        "",
    };

    /*
      =====================================================
      PROFILE FILE INFORMATION
      =====================================================
    */

    if (profileFile) {
      console.log(
        "Selected profile file:",
        profileFile.name,
        profileFile.type,
        profileFile.size
      );
    }

    console.log(
      "Saving PartnerLoginInfo:",
      dataToSave
    );

    /* =====================================================
       SEND TO PHP API
    ===================================================== */

    const response =
      await fetch(API_URL, {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",
        },

        body:
          JSON.stringify(
            dataToSave
          ),
      });

    const responseText =
      await response.text();

    console.log(
      "Partner API response:",
      responseText
    );

    /* =====================================================
       PARSE JSON
    ===================================================== */

    let result;

    try {
      result =
        JSON.parse(
          responseText
        );
    } catch (jsonError) {
      console.error(
        "Partner API returned invalid JSON:",
        responseText
      );

      throw new Error(
        "PHP API returned invalid JSON."
      );
    }

    /* =====================================================
       HTTP ERROR
    ===================================================== */

    if (!response.ok) {
      throw new Error(
        result.message ||
          `Server error: ${response.status}`
      );
    }

    /* =====================================================
       API ERROR
    ===================================================== */

    if (!result.success) {
      throw new Error(
        result.message ||
          "Partner data could not be saved."
      );
    }

    /* =====================================================
       SAVE LOCAL STORAGE
    ===================================================== */

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        dataToSave
      )
    );

    localStorage.setItem(
      "partnerProfile",
      JSON.stringify(
        dataToSave
      )
    );

    localStorage.setItem(
      "partner_user_id",
      userId || ""
    );

    localStorage.setItem(
      "partner_secondary_uuid",
      deviceUuid || ""
    );

    localStorage.setItem(
      "partner_device_uuid",
      deviceUuid || ""
    );

    /*
      Also keep the user ID available
      for the rest of the application.
    */

    if (userId) {
      localStorage.setItem(
        "user_id",
        userId
      );
    }

    /* =====================================================
       CALLBACK
    ===================================================== */

    if (onSave) {
      await onSave(
        dataToSave
      );
    }

    return result;
  };

  /* =========================================================
     SUBMIT
  ========================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* =====================================================
       VALIDATION
    ===================================================== */

    if (!validateForm()) {
      return;
    }

    /* =====================================================
       URL PARAMETERS
    ===================================================== */

    if (!userId || !deviceUuid) {
      alert(
        "User ID or Device UUID is missing."
      );

      return;
    }

    try {
      setIsSubmitting(true);

      await savePartnerData();

      /*
        Move to next partner registration page.
      */

      goTo(
        "/selectworkpage"
      );
    } catch (submitError) {
      console.error(
        "Unable to save partner information:",
        submitError
      );

      alert(
        submitError.message ||
          "Unable to save information."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /* =========================================================
     BACK
  ========================================================= */

  const handleBack = () => {
    if (onBack) {
      onBack();

      return;
    }

    navigate(-1);
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <div className="show-data-page">
        <div className="show-data-card">
          <SplashScreen />
        </div>
      </div>
    );
  }

  /* =========================================================
     ERROR
  ========================================================= */

  if (error) {
    return (
      <div className="show-data-page">
        <div className="show-data-card">
          <h2>
            Error
          </h2>

          <p>
            {error}
          </p>

          <button
            type="button"
            onClick={() =>
              window.location.reload()
            }
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* =========================================================
     NO USER
  ========================================================= */

  if (!user) {
    return (
      <div className="show-data-page">
        <div className="show-data-card">
          <h2>
            No user data found
          </h2>
        </div>
      </div>
    );
  }

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className={
        styles.screenWrapper
      }
    >
      <div
        className={
          styles.deviceFrame
        }
      >
        <div
          className={
            styles.scrollContainer
          }
        >

          {/* =================================================
              HEADER
          ================================================= */}

          <header
            className={
              styles.header
            }
          >
            <button
              type="button"
              className={
                styles.backButton
              }
              onClick={
                handleBack
              }
              aria-label="Go Back"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <div
              className={
                styles.stepIndicator
              }
            >
              <span
                className={`${styles.step} ${styles.stepActive}`}
              />

              <span
                className={
                  styles.step
                }
              />

              <span
                className={
                  styles.step
                }
              />

              <span
                className={
                  styles.step
                }
              />
            </div>
          </header>

          {/* =================================================
              MAIN
          ================================================= */}

          <main
            className={
              styles.mainContent
            }
          >

            <h1
              className={
                styles.title
              }
            >
              Provide your personal information
            </h1>

            {/* =================================================
                PROFILE
            ================================================= */}

            {/* <div
              className={
                styles.profileSection
              }
            >
              <button
                type="button"
                className={
                  styles.avatarButton
                }
                onClick={() =>
                  fileInputRef.current?.click()
                }
              >
                {profileImage ? (
                  <img
                    src={
                      profileImage
                    }
                    alt="Profile Preview"
                    className={
                      styles.avatarImage
                    }
                  />
                ) : user.profile_url ? (
                  <img
                    src={
                      user.profile_url
                    }
                    alt="Profile"
                    className={
                      styles.avatarImage
                    }
                  />
                ) : (
                  <div
                    className={
                      styles.avatarPlaceholder
                    }
                  >
                    <svg
                      width="44"
                      height="44"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#222"
                      strokeWidth="1.8"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />

                      <circle
                        cx="12"
                        cy="7"
                        r="4"
                      />
                    </svg>
                  </div>
                )}
              </button>

              <input
                type="file"
                ref={
                  fileInputRef
                }
                onChange={
                  handleImageChange
                }
                accept="image/*"
                className={
                  styles.hiddenFileInput
                }
              />

              <button
                type="button"
                className={
                  styles.uploadText
                }
                onClick={() =>
                  fileInputRef.current?.click()
                }
              >
                Upload Profile
              </button>

              {errors.profileImage && (
                <span
                  className={
                    styles.errorText
                  }
                >
                  {
                    errors.profileImage
                  }
                </span>
              )}
            </div> */}

            {/* =================================================
                FORM
            ================================================= */}

            <form
              onSubmit={
                handleSubmit
              }
              className={
                styles.formGrid
              }
              noValidate
            >

              {/* =================================================
                  NAME
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.floatingLabel
                  }
                >
                  Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={
                    formData.name
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter full name"
                  autoComplete="name"
                  className={`${styles.inputField} ${
                    errors.name
                      ? styles.inputError
                      : ""
                  }`}
                />

                {errors.name && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {
                      errors.name
                    }
                  </span>
                )}
              </div>

              {/* =================================================
                  PHONE
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.floatingLabel
                  }
                >
                  Phone No
                </label>

                <input
                  type="tel"
                  name="phone"
                  value={
                    formData.phone
                  }
                  onChange={
                    handleChange
                  }
                  maxLength={13}
                  inputMode="numeric"
                  placeholder="Enter phone number"
                  autoComplete="tel"
                  className={`${styles.inputField} ${
                    errors.phone
                      ? styles.inputError
                      : ""
                  }`}
                />

                {errors.phone && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {
                      errors.phone
                    }
                  </span>
                )}
              </div>

              {/* =================================================
                  EMAIL
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.floatingLabel
                  }
                >
                  Email Id
                </label>

                <input
                  type="email"
                  name="email"
                  value={
                    formData.email
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter email address"
                  autoComplete="email"
                  className={`${styles.inputField} ${
                    errors.email
                      ? styles.inputError
                      : ""
                  }`}
                />

                {errors.email && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {
                      errors.email
                    }
                  </span>
                )}
              </div>

              {/* =================================================
                  PASSWORD
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.floatingLabel
                  }
                >
                  Password
                </label>

                <div
                  style={{
                    position:
                      "relative",
                  }}
                >
                  <input
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    name="password"
                    value={
                      formData.password
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Create password"
                    autoComplete="new-password"
                    className={`${styles.inputField} ${
                      errors.password
                        ? styles.inputError
                        : ""
                    }`}
                    style={{
                      paddingRight:
                        "45px",
                    }}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (previous) =>
                          !previous
                      )
                    }
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    style={{
                      position:
                        "absolute",
                      right:
                        "12px",
                      top:
                        "50%",
                      transform:
                        "translateY(-50%)",
                      border:
                        "none",
                      background:
                        "transparent",
                      cursor:
                        "pointer",
                      fontSize:
                        "18px",
                    }}
                  >
                    {showPassword
                      ? "◉"
                      : "◌"}
                  </button>
                </div>

                {errors.password && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {
                      errors.password
                    }
                  </span>
                )}
              </div>

              {/* =================================================
                  CONFIRM PASSWORD
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.floatingLabel
                  }
                >
                  Confirm Password
                </label>

                <div
                  style={{
                    position:
                      "relative",
                  }}
                >
                  <input
                    type={
                      showConfirmPassword
                        ? "text"
                        : "password"
                    }
                    name="confirmPassword"
                    value={
                      formData.confirmPassword
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    className={`${styles.inputField} ${
                      errors.confirmPassword
                        ? styles.inputError
                        : ""
                    }`}
                    style={{
                      paddingRight:
                        "45px",
                    }}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        (previous) =>
                          !previous
                      )
                    }
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                    style={{
                      position:
                        "absolute",
                      right:
                        "12px",
                      top:
                        "50%",
                      transform:
                        "translateY(-50%)",
                      border:
                        "none",
                      background:
                        "transparent",
                      cursor:
                        "pointer",
                      fontSize:
                        "18px",
                    }}
                  >
                    {showConfirmPassword
                      ? "◉"
                      : "◌"}
                  </button>
                </div>

                {errors.confirmPassword && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {
                      errors.confirmPassword
                    }
                  </span>
                )}
              </div>

              {/* =================================================
                  ADDRESS
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.floatingLabel
                  }
                >
                  Address
                </label>

                <input
                  type="text"
                  name="address"
                  value={
                    formData.address
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter current address"
                  autoComplete="street-address"
                  className={`${styles.inputField} ${
                    errors.address
                      ? styles.inputError
                      : ""
                  }`}
                />

                {errors.address && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {
                      errors.address
                    }
                  </span>
                )}
              </div>

              {/* =================================================
                  AGE + GENDER
              ================================================= */}

              <div
                className={
                  styles.rowGrid
                }
              >
                {/* AGE */}

                <div
                  className={
                    styles.fieldGroup
                  }
                >
                  <label
                    className={
                      styles.floatingLabel
                    }
                  >
                    Age
                  </label>

                  <input
                    type="number"
                    name="age"
                    value={
                      formData.age
                    }
                    onChange={
                      handleChange
                    }
                    min="18"
                    max="100"
                    className={`${styles.inputField} ${
                      errors.age
                        ? styles.inputError
                        : ""
                    }`}
                  />

                  {errors.age && (
                    <span
                      className={
                        styles.errorText
                      }
                    >
                      {
                        errors.age
                      }
                    </span>
                  )}
                </div>

                {/* GENDER */}

                <div
                  className={
                    styles.fieldGroup
                  }
                >
                  <label
                    className={
                      styles.floatingLabel
                    }
                  >
                    Gender
                  </label>

                  <select
                    name="gender"
                    value={
                      formData.gender
                    }
                    onChange={
                      handleChange
                    }
                    className={`${styles.inputField} ${styles.selectField}`}
                  >
                    <option value="">
                      Select gender
                    </option>

                    <option value="Male">
                      Male
                    </option>

                    <option value="Female">
                      Female
                    </option>

                    <option value="Other">
                      Other
                    </option>
                  </select>

                  {errors.gender && (
                    <span
                      className={
                        styles.errorText
                      }
                    >
                      {
                        errors.gender
                      }
                    </span>
                  )}
                </div>
              </div>

              {/* =================================================
                  BIO
              ================================================= */}

              <div
                className={
                  styles.fieldGroup
                }
              >
                <label
                  className={
                    styles.floatingLabel
                  }
                >
                  Bio
                </label>

                <textarea
                  name="bio"
                  value={
                    formData.bio
                  }
                  onChange={
                    handleChange
                  }
                  rows="4"
                  placeholder="Tell us about your work..."
                  className={
                    styles.textareaField
                  }
                />

                {errors.bio && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {
                      errors.bio
                    }
                  </span>
                )}
              </div>

              {/* =================================================
                  SAVE BUTTON
              ================================================= */}

              <div
                className={
                  styles.actionWrapper
                }
              >
                <button
                  type="submit"
                  className={
                    styles.saveBtn
                  }
                  disabled={
                    isSubmitting
                  }
                >
                  {isSubmitting
                    ? "Saving..."
                    : "Save & Next"}
                </button>
              </div>

            </form>
          </main>
        </div>
      </div>
    </div>
  );
}